/**
 * Política de precarga en RAM para scroll fluido.
 * Usa deviceMemory (y fallbacks phone/constrained): si hay RAM, se usa.
 *
 * Preview: ideal estudio completo (~decenas de MB WebP).
 * DIAG: serie actual completa si cabe; si no, ventana alrededor del corte.
 *
 * Orden de warmup: interleaved skeleton → ventana direccional → fill scope.
 */
import { RENDER_MODE } from './renderMode'
import { getResourceHints } from './mobileDownloadProfile'

/**
 * @typedef {{
 *   scope: 'study'|'series'|'window',
 *   maxFrames: number,
 *   windowRadius: number,
 *   forwardRadius: number,
 *   backRadius: number,
 *   interleaveStep: number,
 *   velocityBoost: boolean,
 *   concurrency: number,
 *   imageCacheMaxEntries: number,
 *   deviceMemory: number|null,
 * }} ScrollWarmupPolicy
 */

/**
 * @param {{
 *   renderMode?: string|null,
 *   seriesLength?: number,
 *   studyLength?: number,
 * }} [opts]
 * @returns {ScrollWarmupPolicy}
 */
export function getScrollWarmupPolicy(opts = {}) {
  const h = getResourceHints()
  const seriesLen = Math.max(0, Number(opts.seriesLength) || 0)
  const studyLen = Math.max(seriesLen, Number(opts.studyLength) || 0)
  const isPreview =
    opts.renderMode === RENDER_MODE.PREVIEW ||
    String(opts.renderMode || '').toUpperCase() === 'PREVIEW'

  let maxPreview = 400
  let maxDiagSeries = 100
  let concurrency = 2
  let forwardRadius = 12
  let backRadius = 4
  let interleaveStep = 10
  let velocityBoost = false

  if (h.deviceMemory != null) {
    if (h.deviceMemory <= 2) {
      maxPreview = 150
      maxDiagSeries = 40
      concurrency = 2
      forwardRadius = 8
      backRadius = 3
      interleaveStep = 10
      velocityBoost = false
    } else if (h.deviceMemory <= 4) {
      maxPreview = 900
      maxDiagSeries = 150
      concurrency = 3
      forwardRadius = 12
      backRadius = 4
      interleaveStep = 10
      velocityBoost = false
    } else if (h.deviceMemory <= 8) {
      maxPreview = 5000
      maxDiagSeries = 500
      concurrency = 4
      forwardRadius = 20
      backRadius = 5
      interleaveStep = 8
      velocityBoost = true
    } else if (h.deviceMemory <= 16) {
      maxPreview = 10000
      maxDiagSeries = 900
      concurrency = 6
      forwardRadius = 40
      backRadius = 8
      interleaveStep = 5
      velocityBoost = true
    } else {
      maxPreview = 20000
      maxDiagSeries = 2000
      concurrency = 8
      forwardRadius = 40
      backRadius = 8
      interleaveStep = 5
      velocityBoost = true
    }
  } else if (h.phone) {
    maxPreview = 220
    maxDiagSeries = 60
    concurrency = 2
    forwardRadius = 8
    backRadius = 3
    interleaveStep = 10
    velocityBoost = false
  } else if (h.constrained) {
    maxPreview = 800
    maxDiagSeries = 180
    concurrency = 3
    forwardRadius = 12
    backRadius = 4
    interleaveStep = 10
    velocityBoost = false
  } else {
    maxPreview = 5000
    maxDiagSeries = 500
    concurrency = 4
    forwardRadius = 20
    backRadius = 5
    interleaveStep = 8
    velocityBoost = true
  }

  if (h.saveData) {
    maxPreview = Math.min(maxPreview, 120)
    maxDiagSeries = Math.min(maxDiagSeries, 40)
    concurrency = Math.min(concurrency, 2)
    forwardRadius = Math.min(forwardRadius, 6)
    backRadius = Math.min(backRadius, 2)
    interleaveStep = Math.max(interleaveStep, 10)
    velocityBoost = false
  }

  if (isPreview) {
    let scope = 'window'
    // Low-end: nunca study-full
    const allowStudy =
      h.deviceMemory == null
        ? !h.phone && !h.constrained
        : h.deviceMemory > 4
    if (allowStudy && studyLen > 0 && studyLen <= maxPreview) scope = 'study'
    else if (seriesLen > 0 && seriesLen <= maxPreview) scope = 'series'
    else scope = 'window'

    const maxFrames = maxPreview
    const windowRadius = Math.max(
      forwardRadius + backRadius,
      Math.floor(maxFrames / 2)
    )
    return {
      scope,
      maxFrames,
      windowRadius,
      forwardRadius,
      backRadius,
      interleaveStep,
      velocityBoost,
      concurrency,
      imageCacheMaxEntries: Math.min(
        Math.max(
          maxFrames + 40,
          seriesLen + 20,
          studyLen > 0 && scope === 'study' ? studyLen + 40 : 120
        ),
        20000
      ),
      deviceMemory: h.deviceMemory,
    }
  }

  // DIAGNOSTIC: nunca study-full
  let scope = 'window'
  if (seriesLen > 0 && seriesLen <= maxDiagSeries) scope = 'series'
  const maxFrames = maxDiagSeries
  const windowRadius = Math.max(
    forwardRadius + backRadius,
    Math.floor(maxFrames / 2)
  )
  return {
    scope,
    maxFrames,
    windowRadius,
    forwardRadius: Math.min(forwardRadius, 24),
    backRadius: Math.min(backRadius, 6),
    interleaveStep: Math.max(interleaveStep, 8),
    velocityBoost,
    concurrency,
    imageCacheMaxEntries: Math.min(Math.max(maxFrames + 40, 80), 4000),
    deviceMemory: h.deviceMemory,
  }
}

/**
 * Ventana asimétrica: +forward en dirección del gesto, −back al revés.
 * @param {ScrollWarmupPolicy} policy
 * @param {number} length
 * @param {number} center
 * @param {number} [direction] +1 adelante / −1 atrás
 * @param {{ boostForward?: number }} [opts]
 * @returns {number[]}
 */
export function buildDirectionalWarmupOrder(
  policy,
  length,
  center,
  direction = 1,
  opts = {}
) {
  const n = Math.max(0, length | 0)
  if (n <= 0) return []
  const c = Math.max(0, Math.min(n - 1, Math.round(center) || 0))
  const dir = direction < 0 ? -1 : 1
  let fwd = Math.max(1, (policy && policy.forwardRadius) || 12)
  const back = Math.max(0, (policy && policy.backRadius) || 4)
  if (opts.boostForward > 0) {
    fwd = Math.min(
      fwd + Math.round(Number(opts.boostForward) || 0),
      (policy && policy.windowRadius) || fwd * 2
    )
  }
  const out = [c]
  const seen = new Set([c])
  const push = (i) => {
    if (i < 0 || i >= n || seen.has(i)) return
    seen.add(i)
    out.push(i)
  }
  const maxD = Math.max(fwd, back)
  for (let d = 1; d <= maxD; d++) {
    if (d <= fwd) push(c + dir * d)
    if (d <= back) push(c - dir * d)
  }
  return out
}

/**
 * Esqueleto intercalado: cada `interleaveStep` cortes, anclado en center.
 * @param {ScrollWarmupPolicy} policy
 * @param {number} length
 * @param {number} center
 * @returns {number[]}
 */
export function buildInterleavedWarmupOrder(policy, length, center) {
  const n = Math.max(0, length | 0)
  if (n <= 0) return []
  const c = Math.max(0, Math.min(n - 1, Math.round(center) || 0))
  const step = Math.max(2, (policy && policy.interleaveStep) || 10)
  const phase = ((c % step) + step) % step
  const out = []
  const seen = new Set()
  const push = (i) => {
    if (i < 0 || i >= n || seen.has(i)) return
    seen.add(i)
    out.push(i)
  }
  push(c)
  // Hacia afuera por múltiplos del step con misma fase
  for (let d = step; d < n; d += step) {
    push(c + d)
    push(c - d)
  }
  // Resto de la fase (por si center no cae exacto en grid global)
  for (let i = phase; i < n; i += step) {
    push(i)
  }
  return out
}

/**
 * Fill desde center hacia afuera (study/series completo hasta maxFrames).
 * @param {number} length
 * @param {number} center
 * @param {number} maxFrames
 * @returns {number[]}
 */
function buildRadialFillOrder(length, center, maxFrames) {
  const n = Math.max(0, length | 0)
  if (n <= 0) return []
  const c = Math.max(0, Math.min(n - 1, Math.round(center) || 0))
  const out = [c]
  for (let d = 1; d < n; d++) {
    const a = c + d
    const b = c - d
    if (a < n) out.push(a)
    if (b >= 0) out.push(b)
  }
  const max = maxFrames > 0 ? maxFrames : out.length
  return out.slice(0, max)
}

/**
 * Orden compuesto: interleaved → direccional → fill scope.
 * @param {ScrollWarmupPolicy} policy
 * @param {number} length
 * @param {number} center
 * @param {{ direction?: number, boostForward?: number }} [opts]
 * @returns {number[]}
 */
export function buildWarmupIndexOrder(policy, length, center, opts = {}) {
  const n = Math.max(0, length | 0)
  if (n <= 0) return []
  const c = Math.max(0, Math.min(n - 1, Math.round(center) || 0))
  const direction = opts.direction != null ? opts.direction : 1
  const max =
    policy && policy.maxFrames > 0 ? policy.maxFrames : n

  const seen = new Set()
  const out = []
  const append = (list) => {
    for (let i = 0; i < list.length; i++) {
      const idx = list[i]
      if (seen.has(idx)) continue
      seen.add(idx)
      out.push(idx)
      if (out.length >= max) return true
    }
    return false
  }

  const interleaved = buildInterleavedWarmupOrder(policy, n, c)
  if (append(interleaved)) return out

  const directional = buildDirectionalWarmupOrder(
    policy,
    n,
    c,
    direction,
    { boostForward: opts.boostForward }
  )
  if (append(directional)) return out

  const scope = policy && policy.scope
  if (scope === 'study' || scope === 'series') {
    append(buildRadialFillOrder(n, c, max))
  } else {
    // window: ya cubierto por directional; opcional radial corto
    const radius = Math.max(
      1,
      (policy && policy.windowRadius) ||
        ((policy && policy.forwardRadius) || 12) +
          ((policy && policy.backRadius) || 4)
    )
    const win = [c]
    for (let d = 1; d <= radius; d++) {
      if (c + d < n) win.push(c + d)
      if (c - d >= 0) win.push(c - d)
    }
    append(win)
  }

  return out.slice(0, max)
}

export default getScrollWarmupPolicy
